import { ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Airplane, ClockCounterClockwise, Timer } from '@phosphor-icons/react';
import { StatusChip } from '@/components/core';
import { APP_PATHS, MY_TIME_OFF_TABS, SERVICE_REQUEST_TABS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { REQUEST_TYPES } from '@/modules/service-requests/shared/request-types';
import { useGetMyAttendanceCorrections } from '@/modules/time-attendance/attendance/hooks/use-attendance';
import { useGetLeaveRequests } from '@/modules/time-off/my-time-off/hooks/use-leave';
import { useGetToilRequests } from '@/modules/time-off/my-toil/hooks/use-toil';
import { useAuthStore } from '@/state/store/auth';
import { hasAccess } from '@/state/store/auth/use-is-protected';
import { HomeCard, HomeList, HomeListRow } from '../home-card/home-card';

/** Statuses after which a request needs nothing more from anyone. */
const FINAL_STATUSES = new Set([
  'Approved',
  'Rejected',
  'Withdrawn',
  'Cancelled',
  'Generated',
  'Delivered',
  'Completed',
]);
const isOpen = (status?: string) => Boolean(status) && !FINAL_STATUSES.has(status as string);

/** Enough of each kind to fill the card's four rows. */
const PER_KIND = 4;
const PENDING = { Status: 'Pending' };

type RequestRow = {
  key: string;
  icon: ReactNode;
  title: string;
  meta: string;
  status: string;
  to: string;
  /** For the newest-first order (a date or an instant, compared as text). */
  sortKey: string;
};

/**
 * Your requests (Component Map › Your requests): the user's open leave, TOIL, attendance correction and
 * service requests with their status, newest first; each opens its own page.
 */
export const YourRequestsCard = ({ userId }: { userId: string }) => {
  const { t } = useTranslation();
  const canLeave = useHasPermission(HRM_PERMISSIONS.TIME_OFF.VIEW_OWN);
  const canToil = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_VIEW_OWN);
  const canAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_ATTENDANCE);
  const user = useAuthStore((state) => state.user);
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  const serviceTypes = useMemo(
    () =>
      REQUEST_TYPES.filter((config) =>
        hasAccess({ user, organizationId, permissions: [config.viewOwn] })
      ),
    [user, organizationId]
  );

  const leaves = useGetLeaveRequests(
    { pageNo: 1, pageSize: PER_KIND, filter: { UserId: userId, ...PENDING } },
    canLeave
  );
  const toils = useGetToilRequests(
    { pageNo: 1, pageSize: PER_KIND, filter: { UserId: userId, ...PENDING } },
    canToil
  );
  const corrections = useGetMyAttendanceCorrections(
    { pageNo: 1, pageSize: PER_KIND, filter: PENDING },
    canAttendance
  );
  const services = useQuery({
    queryKey: ['home', 'open-service-requests', userId, serviceTypes.map((config) => config.type)],
    queryFn: async () =>
      (
        await Promise.all(
          serviceTypes.map(async (config) =>
            (await config.listOwn(userId)).map((item) => ({ config, item }))
          )
        )
      )
        .flat()
        .filter(({ item }) => isOpen(item.Status)),
    enabled: Boolean(userId) && serviceTypes.length > 0,
    staleTime: 60 * 1000,
  });

  const leaveItems =
    (
      leaves.data as {
        LeaveRequests?: {
          items: {
            ItemId: string;
            Title: string;
            LeaveCategory?: string;
            StartDate: string;
            EndDate: string;
            Status: string;
          }[];
        };
      }
    )?.LeaveRequests?.items ?? [];
  const toilItems =
    (
      toils.data as {
        ToilRequests?: {
          items: { ItemId: string; ToilDate: string; ToilType?: string; Status: string }[];
        };
      }
    )?.ToilRequests?.items ?? [];
  const correctionItems =
    (
      corrections.data as {
        AttendanceCorrections?: {
          items: { ItemId: string; CorrectionDate: string; Status: string }[];
        };
      }
    )?.AttendanceCorrections?.items ?? [];

  const rows: RequestRow[] = [
    ...(canLeave
      ? leaveItems.map((leave) => ({
          key: `leave-${leave.ItemId}`,
          icon: <Airplane />,
          title: leave.LeaveCategory || leave.Title,
          meta: `${formatDateOnly(leave.StartDate)} – ${formatDateOnly(leave.EndDate)}`,
          status: leave.Status,
          to: `${APP_PATHS.MY_TIME_OFF}?tab=${MY_TIME_OFF_TABS.HISTORY}`,
          sortKey: leave.StartDate,
        }))
      : []),
    ...(canToil
      ? toilItems.map((toil) => ({
          key: `toil-${toil.ItemId}`,
          icon: <Timer />,
          title: t('TOIL'),
          meta: formatDateOnly(toil.ToilDate),
          status: toil.Status,
          to: `${APP_PATHS.MY_TIME_OFF}?tab=${MY_TIME_OFF_TABS.TOIL}`,
          sortKey: toil.ToilDate,
        }))
      : []),
    ...(canAttendance
      ? correctionItems.map((correction) => ({
          key: `correction-${correction.ItemId}`,
          icon: <ClockCounterClockwise />,
          title: t('HOME_ATTENDANCE_CORRECTION'),
          meta: formatDateOnly(correction.CorrectionDate),
          status: correction.Status,
          to: APP_PATHS.ATTENDANCE,
          sortKey: correction.CorrectionDate,
        }))
      : []),
    ...(services.data ?? []).map(({ config, item }) => {
      const TypeIcon = config.icon;
      return {
        key: `${config.type}-${item.ItemId}`,
        icon: <TypeIcon />,
        title: t(config.label),
        meta: item.AppliedDateUtc ? formatDateOnly(item.AppliedDateUtc.slice(0, 10)) : '',
        status: item.Status,
        to: `${APP_PATHS.SERVICE_REQUESTS}?tab=${SERVICE_REQUEST_TABS.MY}&type=${config.type}&record=${item.ItemId}`,
        sortKey: item.AppliedDateUtc ?? '',
      };
    }),
  ].sort((a, b) => b.sortKey.localeCompare(a.sortKey));

  const isLoading =
    (canLeave && leaves.isLoading) ||
    (canAttendance && corrections.isLoading) ||
    (canToil && toils.isLoading);

  return (
    <HomeCard title={t('HOME_YOUR_REQUESTS')} count={rows.length}>
      <HomeList isLoading={isLoading} emptyText={t('HOME_NO_OPEN_REQUESTS')}>
        {rows.map((row) => (
          <HomeListRow
            key={row.key}
            icon={row.icon}
            title={row.title}
            meta={row.meta}
            to={row.to}
            end={<StatusChip status={row.status}>{t(row.status.toUpperCase())}</StatusChip>}
          />
        ))}
      </HomeList>
    </HomeCard>
  );
};
